import _ from 'lodash';
import shortid from 'shortid';
import Operators from './Operators.json';
import { searchCondition } from 'omega-rtc/utils/condition-utils';
import type { Condition } from 'omega-rtc/types/trident-campaigns';

const LeafOperators = Operators.map(o => o.sym);
const getOperatorType = operator => _.find(Operators, o => o.sym === operator);
export const getOperatorDisp = operator => _.get(getOperatorType(operator), 'disp');

export const addUidToRule = (rule) => {
  if (rule.uid === undefined) rule.uid = shortid.generate();
  if (_.isArray(rule.conditions)) rule.conditions.forEach(cond => addUidToRule(cond));
};

export const removeUidFromRule = (rule) => {
  if (rule.uid !== undefined) delete rule.uid;
  if (_.isArray(rule.conditions)) rule.conditions.forEach(cond => removeUidFromRule(cond));
};

export const getRuleType = (ruleTypes, ruleCode) => {
  let ruleType = _.find(ruleTypes, rt => rt.variableName === ruleCode);
  if (_.isEmpty(ruleType)) {
    // Still showing deprecated variableName on the screen but won't allow to save
    ruleType = {
      variableName: ruleCode,
      noun: ruleCode,
      isDeprecated: true,
    };
  }
  return ruleType;
};

export function validateLHS(ruleTypes: Array, conditionObj: Condition) {
  const deprecatedConds = searchCondition(conditionObj, cond => {
    if (_.isEmpty(cond.lhs)) {
      return false;
    }
    return getRuleType(ruleTypes, cond.lhs).isDeprecated;
  });
  if (deprecatedConds.length > 0) {
    return `'${deprecatedConds[0].lhs}' condition is deprecated`;
  }
  return '';
}

export const getRuleName = (ruleTypes, ruleCode) => _.get(getRuleType(ruleTypes, ruleCode), 'noun');

export const getDefaultOperator = (ruleType) => {
  const defaultOperator = _.isArray(ruleType.allowedOperators) && ruleType.allowedOperators[0];
  return defaultOperator || 'eq';
};

export const isLeafRule = rule => LeafOperators.indexOf(rule.operator) > -1;

export const operatorOptions = ruleType => {
  if (ruleType && ruleType.allowedOperators) {
    return ruleType.allowedOperators.map(o => {
      return { key: o, text: getOperatorDisp(o), value: o };
    });
  }
};

export function findRule(ruleTree, ruleUid, path) {
  const rulePath = path || [];
  rulePath.push(ruleTree.uid);

  if (ruleTree.uid === ruleUid) {
    return { rule: ruleTree, path: rulePath };
  }

  if (ruleTree.conditions) {
    for (const cond of ruleTree.conditions) {
      const { rule, path } = findRule(cond, ruleUid, _.clone(rulePath));
      if (rule) {
        return { rule, path };
      }
    }
  }

  return { rule: null, path: [] };
}

export function humanize(str) {
  return str.toLowerCase()
    .replace(/^[\s_]+|[\s_]+$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/^[a-z]/, m => m.toUpperCase());
}
